import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';
import type {
  FetchCommercialHistoryDto,
  FetchCommercialProductsDto,
  FetchCommercialSettlementsDto,
  FetchCounterpartMaterialsDto,
  ListResponse,
} from '@repo/api';
import {
  fetchCompanyCommercialHistory,
  fetchCompanyCommercialPrices,
  fetchCompanyCommercialProducts,
  fetchCompanyCommercialSettlements,
  fetchCompanyMaterials,
  fetchCompanyStats,
} from '@/api/company/api';
import { TAKE } from '@/lib/constants/pagination';

/**
 * Prefisso unico delle letture commerciali della scheda controparte
 * (statistiche, storico, articoli, prezzi, scadenze): sono dati di documenti e
 * incassi, quindi le invalida il websocket quando cambiano documenti, rate,
 * riconciliazioni o movimenti (`entity-to-query-keys.ts`), non solo
 * l'anagrafica.
 */
export const COMPANY_COMMERCIAL_QUERY_PREFIX = 'company-commercial';

/** Pagina successiva finché le righe caricate non coprono il totale. */
export function nextOffsetPage<T>(
  lastPage: ListResponse<T>,
  allPages: ListResponse<T>[]
): number | undefined {
  if (lastPage.data.length === 0) return undefined;
  const loaded = allPages.reduce((sum, page) => sum + page.data.length, 0);
  return loaded >= lastPage.totalCount ? undefined : allPages.length;
}

export const companyCommercialQueryKeys = {
  all: () => [COMPANY_COMMERCIAL_QUERY_PREFIX],
  stats: (tenantId: string, companyId: string) =>
    queryOptions({
      queryKey: [COMPANY_COMMERCIAL_QUERY_PREFIX, 'stats', tenantId, companyId],
      queryFn: () => fetchCompanyStats(tenantId, companyId),
    }),
  history: (
    tenantId: string,
    companyId: string,
    filters: FetchCommercialHistoryDto,
    pageSize: number = TAKE
  ) =>
    infiniteQueryOptions({
      queryKey: [
        COMPANY_COMMERCIAL_QUERY_PREFIX,
        'history',
        tenantId,
        companyId,
        filters,
        pageSize,
      ],
      queryFn: ({ pageParam }) =>
        fetchCompanyCommercialHistory(tenantId, companyId, {
          ...filters,
          skip: pageParam * pageSize,
          take: pageSize,
        }),
      initialPageParam: 0,
      getNextPageParam: nextOffsetPage,
    }),
  products: (
    tenantId: string,
    companyId: string,
    filters: FetchCommercialProductsDto
  ) =>
    infiniteQueryOptions({
      queryKey: [
        COMPANY_COMMERCIAL_QUERY_PREFIX,
        'products',
        tenantId,
        companyId,
        filters,
      ],
      queryFn: ({ pageParam }) =>
        fetchCompanyCommercialProducts(tenantId, companyId, {
          ...filters,
          skip: pageParam * TAKE,
          take: TAKE,
        }),
      initialPageParam: 0,
      getNextPageParam: nextOffsetPage,
    }),
  prices: (
    tenantId: string,
    companyId: string,
    key: string,
    side: FetchCommercialProductsDto['side']
  ) =>
    infiniteQueryOptions({
      queryKey: [
        COMPANY_COMMERCIAL_QUERY_PREFIX,
        'prices',
        tenantId,
        companyId,
        key,
        side,
      ],
      queryFn: ({ pageParam }) =>
        fetchCompanyCommercialPrices(tenantId, companyId, {
          key,
          side,
          skip: pageParam * TAKE,
          take: TAKE,
        }),
      initialPageParam: 0,
      getNextPageParam: nextOffsetPage,
    }),
  settlements: (
    tenantId: string,
    companyId: string,
    filters: FetchCommercialSettlementsDto
  ) =>
    infiniteQueryOptions({
      queryKey: [
        COMPANY_COMMERCIAL_QUERY_PREFIX,
        'settlements',
        tenantId,
        companyId,
        filters,
      ],
      queryFn: ({ pageParam }) =>
        fetchCompanyCommercialSettlements(tenantId, companyId, {
          ...filters,
          skip: pageParam * TAKE,
          take: TAKE,
        }),
      initialPageParam: 0,
      getNextPageParam: nextOffsetPage,
    }),
  materials: (
    tenantId: string,
    companyId: string,
    filters: FetchCounterpartMaterialsDto
  ) =>
    infiniteQueryOptions({
      queryKey: [
        COMPANY_COMMERCIAL_QUERY_PREFIX,
        'materials',
        tenantId,
        companyId,
        filters,
      ],
      queryFn: ({ pageParam }) =>
        fetchCompanyMaterials(tenantId, companyId, {
          ...filters,
          skip: pageParam * TAKE,
          take: TAKE,
        }),
      initialPageParam: 0,
      getNextPageParam: nextOffsetPage,
    }),
};
